class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === "cart-items") return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement?.getAttribute("name"),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
          }
        })
        .catch((e) => console.error(e));
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const sourceQty = html.querySelector("cart-items");
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => console.error(e));
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset?.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset?.id,
        selector: ".js-contents",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
    ].filter((s) => s.section || s.id === "CartDrawer" || s.id === "cart-icon-bubble" || s.id === "cart-live-region-text");
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);

        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartItems = document.querySelector("cart-items");
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartItems) cartItems.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartFooter) cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle("is-empty", parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const host = document.getElementById(section.id);
          if (!host) return;
          const elementToReplace = host.querySelector(section.selector) || host;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;

        let message = "";
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace("[quantity]", updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);

        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector(".cart-item__name"));
        }

        // ✅ Publish INSIDE where parsedState exists
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });

        // ✅ Auto-add gift after cart updates (guarded)
        setTimeout(() => {
          try {
            this.autoAddGiftIfNeeded(parsedState);
          } catch (e) {
            console.warn("[Gift] autoAdd call failed:", e);
          }
        }, 80);
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement?.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus?.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus?.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : "";
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems?.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems?.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add("hidden"));
  }

  // ✅ Auto-add gift (fixed payload + anti-loop)
  autoAddGiftIfNeeded(parsedState) {
    const GIFT_VARIANT_ID = 46358866264195;
    const THRESHOLD = 4;
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
      mainCartItems.style.opacity = ".75";
    // Prevent loops / spam
    if (this.__autoGiftAddLock) return Promise.resolve();

    // Don't trigger because of gift's own cartUpdate
    if (parsedState && parsedState.items) {
      const updatedHasGift = parsedState.items.some((i) => i.variant_id === GIFT_VARIANT_ID);
      if (updatedHasGift && parsedState.item_count) {
        // still allow future runs, but don't add again
      }
    }

    this.__autoGiftAddLock = true;

    return fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((cart) => {
        const nonGiftCount = cart.items
          .filter((item) => item.variant_id !== GIFT_VARIANT_ID)
          .reduce((sum, item) => sum + item.quantity, 0);

        if (nonGiftCount < THRESHOLD) return;

        const hasGift = cart.items.some((item) => item.variant_id === GIFT_VARIANT_ID);
        if (hasGift) return;

        return fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            items: [
              {
                id: GIFT_VARIANT_ID,
                quantity: 1,
                properties: { _promo: "Free gift (4+ items)", _auto: "true" },
              },
            ],
          }),
        });
      })
      .then((res) => {
        if (res && res.ok) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-auto-add" });
        }
      })
      .catch((e) => console.warn("[Gift] autoAddGiftIfNeeded failed:", e))
      .finally(() => {
        setTimeout(() => (this.__autoGiftAddLock = false), 100);
        mainCartItems.style.opacity = "1";
      });
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

async function removeLineByKey(lineKey) {
  await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: lineKey, quantity: 0 }),
  });
}

(function autoRemoveGiftLineInDrawer() {
  if (window.__autoRemoveGiftInit) return;
  window.__autoRemoveGiftInit = true;

  const getCartItemsEl = () =>
    document.querySelector("cart-drawer-items") || document.querySelector("cart-items");

  const getDrawerRoot = () =>
    document.querySelector("cart-drawer") ||
    document.querySelector("#CartDrawer") ||
    document.querySelector(".cart-drawer");

  const run = debounce(() => {
    const drawer = getDrawerRoot();
    const cartItemsEl = getCartItemsEl();

    if (!drawer || !cartItemsEl || typeof cartItemsEl.updateQuantity !== "function") return;

    const mainCartItems =
      document.getElementById("CartDrawer-CartItems") || document.getElementById("main-cart-items");
    if (mainCartItems && mainCartItems.classList.contains("cart__items--disabled")) return;

    const targets = drawer.querySelectorAll('cart-remove-button[data-auto-remove="true"]');
    if (!targets.length) return;

    targets.forEach((el) => {
      el.removeAttribute("data-auto-remove");

      const index = Number(el.dataset.index);
      if (!index) return;

      cartItemsEl.updateQuantity(index, 0, "updates[]", el.getAttribute("data-variant-id"));
    });
  }, 80);

  run();

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, run);
  }

  const drawer = getDrawerRoot();
  if (drawer) new MutationObserver(run).observe(drawer, { childList: true, subtree: true });
})();

document.addEventListener(
  "click",
  (e) => {
    const btn = e.target.closest(".quantity__button");
    if (!btn) return;

    const wrapper = btn.closest("quantity-input");
    if (!wrapper) return;

    const input = wrapper.querySelector("input.quantity__input");
    if (!input) return;

    const isBroken = input.disabled || input.hasAttribute("readonly");
    if (!isBroken) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    input.disabled = false;

    const step = Number(input.step || 1);
    const min = Number(input.min || 0);
    const maxAttr = input.getAttribute("max");
    const max = maxAttr !== null && maxAttr !== "" ? Number(maxAttr) : Infinity;

    let val = Number(input.value || 0);

    if (btn.name === "plus") val = Math.min(val + step, max);
    if (btn.name === "minus") val = Math.max(val - step, min);

    input.value = val;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  },
  true
);
